import { API_BASE_URL } from './APIService';
import { v4 as uuidv4 } from 'uuid';
/**
 * Google Calendar integration configuration type
 */
interface GoogleCalendarConfig {
  clientId: string;
  redirectUri: string;
  scopes: string[];
}

/**
 * Fetches the Google OAuth configuration from the backend
 */
const fetchGoogleIntegrationConfig = async (): Promise<GoogleCalendarConfig> => {
  try {
    const response = await fetch(`${API_BASE_URL}/v2/provider/config`);
    const data = await response.json();

    if (data.status === 200 && data.data.googleIntegration) {
      return data.data.googleIntegration;
    }

    throw new Error('Failed to retrieve Google Integration configuration');
  } catch (error) {
    console.error('Error fetching Google Integration config:', error);
    throw error;
  }
};

/**
 * Initiates the OAuth 2.0 authorization code flow for Google integration.
 * This redirects the user to Google's authorization page after fetching config from server.
 */
export const initiateGoogleIntegration = async () => {
  try {
    // Fetch configuration from server
    const config = await fetchGoogleIntegrationConfig();

    // State parameter for security (prevents CSRF)
    const state = uuidv4();

    // Store state in localStorage to verify when the user returns
    localStorage.setItem('google_oauth_state', state);

    // Determine the redirect URI - use our frontend route instead of the backend one
    const redirectUri = `${window.location.origin}/google-auth-callback`;

    // Construct the authorization URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.append('client_id', config.clientId);
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', config.scopes.join(' '));
    authUrl.searchParams.append('state', state);
    authUrl.searchParams.append('access_type', 'offline');
    authUrl.searchParams.append('prompt', 'consent');

    // Redirect to Google's authorization page
    window.location.href = authUrl.toString();
  } catch (error) {
    console.error('Failed to initiate Google authorization:', error);
    // Handle error - perhaps show a notification to user
    throw error;
  }
};
